// Apolo IDE — SubagentsRailSection (Fase 2 + S5).
//
// Seção "Subagentes" do rail direito do Code chat. Mostra DOIS conjuntos:
//   1. dispatch/orchestrator (multi-agent legado) via useActiveDispatches;
//   2. subagents S5 desta sessão (explore/bash/browser) via useSubagentRuns —
//      contexto isolado, só o `summary` cruza; status ao vivo (running→done).
// Renderiza null quando NÃO há nenhum dos dois (sessão single-agent, caso comum).
//
// Regra do Pedro: cor SÓ no ícone e no estado, hover neutro. Cada kind tem um
// ícone próprio (explore=lupa, bash=terminal, browser=globo) — o status é texto
// colorido (statusTone). ARIA de status em cada linha.

import { Bot, Search, Terminal, Globe } from 'lucide-react';
import useActiveDispatches from '../../../hooks/useActiveDispatches.js';
import useSubagentRuns from '../../../hooks/useSubagentRuns.js';
import { statusTone, fmtTokens, collectSubagents } from '../agents/agentFormat.js';
import './redesign.css';

const KIND_ICON = { explore: Search, bash: Terminal, browser: Globe };
const KIND_LABEL = { explore: 'Explore', bash: 'Bash', browser: 'Browser' };

function SubagentRunRow({ run }) {
  const Icon = KIND_ICON[run.kind] || Bot;
  const tone = statusTone(run.status);
  const isRunning = run.status === 'running';
  const title = `${KIND_LABEL[run.kind] || run.kind}: ${run.input || ''}`;
  return (
    <div className="rx-out" title={title}>
      <span className={`rx-oic rx-gold ${isRunning ? 'animate-pulse' : ''}`} aria-hidden="true">
        <Icon size={15} />
      </span>
      <div className="rx-ot">
        <div className="rx-n">
          {KIND_LABEL[run.kind] || run.kind}
          {run.input ? <span className="rx-m"> · {String(run.input).slice(0, 40)}</span> : null}
        </div>
        <div className="rx-m" role="status" aria-label={`status ${tone.label}`}>
          <span className={tone.text}>{tone.label}</span>
          {run.summary && run.status !== 'running' ? ` · ${String(run.summary).split('\n')[0].slice(0, 48)}` : ''}
        </div>
      </div>
    </div>
  );
}

export default function SubagentsRailSection({ sessionId }) {
  const { workflows, recent } = useActiveDispatches({ sessionId });
  const dispatched = collectSubagents(workflows, recent);
  const s5runs = useSubagentRuns(sessionId);

  const total = dispatched.length + s5runs.length;
  if (total === 0) return null;

  return (
    <section aria-label="Subagentes" className="mt-4 flex flex-col gap-2">
      <div className="rx-rail-h">
        <Bot size={13} aria-hidden="true" />
        Subagentes
        <span className="rx-cnt" aria-label={`${total} subagentes`}>{total}</span>
      </div>
      <div className="rx-rail-list">
        {s5runs.map((run) => <SubagentRunRow key={run.id} run={run} />)}
        {dispatched.map((a) => {
          const tone = statusTone(a.status);
          const isRunning = a.status === 'running';
          return (
            <div key={a.id || a.goal} className="rx-out" title={a.goal}>
              <span className={`rx-oic rx-gold ${isRunning ? 'animate-pulse' : ''}`} aria-hidden="true">
                <Bot size={15} />
              </span>
              <div className="rx-ot">
                <div className="rx-n">{a.goal}</div>
                <div className="rx-m" role="status" aria-label={`status ${tone.label}`}>
                  <span className={tone.text}>{tone.label}</span>
                  {a.model && a.model !== 'unknown' ? ` · ${a.model}` : ''}
                  {a.tokens_used > 0 ? ` · ${fmtTokens(a.tokens_used)} tok` : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
